// @flow
import React, {Fragment} from 'react';
import Tooltip from '@homeaway/react-tooltip';
import {UrgencyMessage} from '@homeaway/react-urgency-messaging';
import Experiment from '@homeaway/react-experiment';
import GeoDistance from '../GeoDistance/GeoDistance';
import Icon from '../Icon/Icon';
import {filter as filterGraphQlAnywhere} from 'graphql-anywhere';
import Price, {fragments as priceFragments} from '../Price/Price';
import Rating, {fragments as ratingFragments} from '../Rating/Rating';
import Details from '../Details/Details';
import {HA_SERP_FREE_CANCELLATION_V2} from '../../abacus/experiments';
import HitFreeCancellation from '../HitFreeCancellation/HitFreeCancellation';
import {FormattedMessage} from 'react-intl';
import HitLabel from '../HitLabel/HitLabel';

import type {Props as HitInfoProps} from './HitInfo';
import {isNumber} from 'lodash';
import type {Badge} from './HitInfo';

type Props = {
    exclusivityBadge: ?Badge,
    instantBookableTitle: string,
    premierPartnerBadge: ?Badge
} & HitInfoProps;

export default function HitInfoDesktop({
    brand,
    premierPartnerBadge,
    exclusivityBadge,
    hit,
    hitIndex,
    intl,
    isDesktop,
    isHovered,
    listing,
    instantBookableTitle,
    isDatelessSearch,
    isMetric,
    fullLocale
}: Props) {
    let tooltipParentNode;
    const {freeCancellationUntil, geoDistance, urgencyMessage} = hit;
    const {
        detailPageUrl,
        instantBookable,
        propertyMetadata: {headline},
        reviewBadges
    } = listing;
    /* istanbul ignore next */
    if (typeof window !== 'undefined') {
        tooltipParentNode = document.getElementsByTagName('body')[0];
    }

    return (
        <div className="HitInfo HitInfo--desktop">
            <div className="HitInfo__content">
                {
                    <UrgencyMessage
                        className="HitInfo__content--urgency"
                        size="s"
                        unitMessage={{iconTextV2: urgencyMessage}}
                    />
                }
                <div className="HitInfo__item">
                    <h4
                        className="HitInfo__headline hover-text"
                        href={detailPageUrl}
                        aria-hidden="true"
                    >
                        {headline}
                    </h4>

                    {isNumber(hitIndex) && (
                        <span className="sr-only">
                            <FormattedMessage
                                id="property"
                                description="Property"
                                defaultMessage="Property"
                            >
                                {(txt) => (
                                    <Fragment>
                                        {txt} {hitIndex + 1}
                                        {':'} {headline}
                                    </Fragment>
                                )}
                            </FormattedMessage>
                        </span>
                    )}
                </div>

                <div className="HitInfo__item">
                    <div className="HitInfo__details">
                        <Details listing={listing} isMetric={isMetric} />
                    </div>
                </div>
                {geoDistance && (
                    <div className="HitInfo__item hidden-xs">
                        <GeoDistance
                            brand={brand}
                            geoDistance={geoDistance}
                            isHighlighted={isHovered}
                        />
                    </div>
                )}
                {(!!exclusivityBadge || freeCancellationUntil) && (
                    <div className="HitInfo__item HitInfo__labels">
                        {!!exclusivityBadge && (
                            <HitLabel
                                label={exclusivityBadge.name}
                                toolTipText={exclusivityBadge.helpText}
                                id={exclusivityBadge.id}
                                parentNode={tooltipParentNode}
                            />
                        )}
                        {freeCancellationUntil && (
                            <Experiment
                                name={HA_SERP_FREE_CANCELLATION_V2}
                                variant={Experiment.ON}
                            >
                                <HitFreeCancellation
                                    isDesktop={isDesktop}
                                    freeCancellationUntil={
                                        freeCancellationUntil
                                    }
                                    formatMessage={intl.formatMessage}
                                    parentNode={tooltipParentNode}
                                />
                            </Experiment>
                        )}
                    </div>
                )}
            </div>
            <div className="HitInfo__infoBar hover-bg">
                <div className="HitInfo__priceSuperlativeGroup">
                    {!!reviewBadges && (
                        <span
                            className="HitInfo__superlative small"
                            aria-hidden="true"
                        >
                            {reviewBadges[0].name}
                        </span>
                    )}
                    <div className="HitInfo__price">
                        {instantBookable && (
                            <Icon
                                name="instant-book"
                                iconClass="InstantBook__icon"
                                title={instantBookableTitle}
                            />
                        )}
                        <Price
                            hit={filterGraphQlAnywhere(
                                priceFragments.SERPHit,
                                hit
                            )}
                            listing={filterGraphQlAnywhere(
                                priceFragments.listing,
                                listing
                            )}
                            useAverage={isDatelessSearch}
                            fullLocale={fullLocale}
                        />
                    </div>
                </div>
                <div className="HitInfo__badgeRatingGroup">
                    {premierPartnerBadge && (
                        <Tooltip
                            tooltipType="popover"
                            content={premierPartnerBadge.helpText}
                            placement="bottom"
                            parentNode={tooltipParentNode}
                        >
                            <div
                                className="HitInfo__premierBadge bg-brand small"
                                data-wdio="premier-partner-badge"
                                tabIndex="-1"
                            >
                                {premierPartnerBadge.name}
                            </div>
                        </Tooltip>
                    )}
                    <div className="HitInfo__rating">
                        <Rating
                            listing={filterGraphQlAnywhere(
                                ratingFragments.listing,
                                listing
                            )}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
